// WebApp-owned AI job tracker state (FRO-10614).
// Per the WebApp/WebAI integration contract, the parent app owns the
// minimized "AI process list": entries are added only when the user clicks
// Minimize. State is in-memory only â€” a page refresh or browser close
// clears the list (the jobs themselves keep running in WebAI).

export const AI_JOB_TYPE = {
    IMAGE_GENERATION: 'image-generation',
    DOCUMENT_EXTRACTION: 'document-extraction'
};

export const AI_JOB_STATUS = {
    NOT_STARTED: 'NOT_STARTED',
    QUEUED: 'QUEUED',
    PROCESSING: 'PROCESSING',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
    CANCELLED: 'CANCELLED'
};

export const ACTIVE_AI_JOB_STATUSES = [
    AI_JOB_STATUS.NOT_STARTED,
    AI_JOB_STATUS.QUEUED,
    AI_JOB_STATUS.PROCESSING
];

export interface IAIJobTrackerJob {
    jobId: string;
    imageUrl?: string | null;
    filename?: string | null;
    status: string;
    resultImageUrl?: string | null;
}

// One tracker group per minimize batch â€” a property can have several groups
// at once. `groupKey` identifies the batch; `routePath` points back to the
// originating page so a click can navigate straight to it.
export interface IAIJobTrackerGroup {
    groupKey: string;
    routePath: string;
    sessionId: string;
    type: string;
    reference: string | null;
    propertyId?: number | null;
    projectId?: number | null;
    previewImageUrl?: string | null;
    jobs: IAIJobTrackerJob[];
    createdAt: number;
}

// Minimized editor session, persisted so the iframe can be restored in the
// exact same state (moved here from the property-edit store so it survives
// that module being unregistered when leaving property routes).
export interface IAIImageSessionState {
    sessionId: string;
    routePath: string;
    reference: string | null;
    // Exactly one of these is set depending on the originating entity type.
    propertyId?: number | null;
    projectId?: number | null;
    module: number;
    uploadType: string;
    language: string;
    isBulkMode: boolean;
    // Resolved URLs for the bulk selection so a restored bulk session can
    // re-send `init-bulk` to WebAI. Stored as plain strings so the session
    // state stays serialisable and decoupled from the picture view-models.
    bulkImages: string[];
    currentImageData: any;
    // Path within the WebAI origin sent on minimize/expand so the parent can
    // reopen the editor in the exact same state. (`trackerUrl` from the same
    // payload is deprecated for WebApp and intentionally not stored.)
    editorUrl: string | null;
    // Set when the user clicked a specific job in the WebApp tracker: the
    // next restore opens /image-iframe?jobId=<uuid> for that job instead of
    // the generic editorUrl.
    pendingJobId?: string | null;
    // Whether the session was minimized (true) or should be expanded (false)
    // when restored on a new page.
    isMinimized: boolean;
}

export interface IAIJobTrackerState {
    groups: Record<string, IAIJobTrackerGroup>;
    sessions: Record<string, IAIImageSessionState>;
}

// WebAI sometimes passes FAL queue statuses through (e.g. in the minimize
// payload), and the PDF iframe sends its per-file bulk statuses â€” map them
// onto the contract's canonical statuses.
const AI_JOB_STATUS_ALIASES: Record<string, string> = {
    IN_QUEUE: AI_JOB_STATUS.QUEUED,
    IN_PROGRESS: AI_JOB_STATUS.PROCESSING,
    IDLE: AI_JOB_STATUS.NOT_STARTED,
    CONVERTING: AI_JOB_STATUS.PROCESSING,
    ANALYZING: AI_JOB_STATUS.PROCESSING,
    COMPLETE: AI_JOB_STATUS.COMPLETED,
    ERROR: AI_JOB_STATUS.FAILED
};

// Single source of truth for mapping WebAI job payloads (minimize postMessage
// jobs and the status endpoint's rows) onto tracked jobs. The job UUID â€”
// `jobId` on minimize jobs, `id` on status rows â€” is the canonical handle.
// Status casing and FAL aliases are normalized so payload variations can't
// break the active/idle detection.
export const normalizeAIJob = (raw: any): IAIJobTrackerJob => {
    const status = String(raw?.status || AI_JOB_STATUS.NOT_STARTED).toUpperCase();
    return {
        jobId: String(raw?.jobId ?? raw?.id ?? ''),
        imageUrl: raw?.imageUrl ?? raw?.originalImageUrl ?? null,
        // Document rows name the PDF as `uploadedPdfName`.
        filename: raw?.filename ?? raw?.uploadedPdfName ?? null,
        status: AI_JOB_STATUS_ALIASES[status] || status,
        resultImageUrl: raw?.resultImageUrl ?? null
    };
};

export const hasActiveAIJobs = (jobs?: IAIJobTrackerJob[]): boolean =>
    !!jobs?.some(job => ACTIVE_AI_JOB_STATUSES.includes(job.status));

export class AIJobTrackerState implements IAIJobTrackerState {
    groups: Record<string, IAIJobTrackerGroup>;
    sessions: Record<string, IAIImageSessionState>;

    constructor() {
        this.groups = {};
        this.sessions = {};
    }
}
